import { RegionCode } from 'google-libphonenumber';
import { ExtendedUseFormReturn } from '../../hooks/providers/useFormHooks';
import { ISupplierModel } from '../Supplier/types';

export enum FormTypes {
  BUSINESS = 'BUSINESS',
  INDI = 'INDIVIDUAL',
}

export enum MoveMoneyTypes {
  WIRE_OUT = 'wire-out',
  MAIL_CHECK = 'mail-check',
  INTERNATIONAL_WIRE_OUT = 'international-wire-out',
  ASK_VENDOR = 'ask-vendor', ACH_OUT_TRANSFER = 'ach-out-transfer',
}

export interface IVendorModel {
  supplierId: string | null,
  supplierEntityType: string,
  name: string,
  email?: string | null,
  methodType?: string | null,
  achAccountData: {
    routingNumber?: string | null,
    accountNumber?: string | null,
    empty?: boolean,
    notEmpty?: boolean
  } | null,
  wireAccountData: {
    routingNumber?: string | null,
    accountNumber?: string | null,
    empty?: boolean,
    notEmpty?: boolean
  } | null,
  supplierAddressData: {
    city: string | null,
    state: string | null,
    line1: string | null,
    line2: string | null,
    postalCode: string | null,
    country: string | null
  } | null,
  notifyOnPayment: boolean | null,
  methodStatusDataList?: {
    methodType: string,
    status: string
  }[] | null,
  'internationalWireAccountData'?: {
    'swiftCode': string,
    'iban': string,
    'internationalPhoneNumber': string,
    phoneNumberCountryCode: RegionCode | null,
    'internationalSupplierAddressData': {
      'city': string,
      'state': string | null,
      'line1': string,
      'line2': string | null,
      'postalCode': string,
      'country': string
    },
  } | null
}

export interface IUpdateVendorFormContainer {
  moveType: MoveMoneyTypes;
  formType: FormTypes;
  vendorData?: IVendorModel;
  isVendorUpdateMandatory?: boolean;
  billId?: string;
  isEditMode?: boolean;
  isLoading: boolean;
  isLoadingDelete?: boolean;
  onVendorDelete: () => void,
  onVendorSubmit: (data: ISupplierModel) => void,
  initialBusinessName?: string;
  customSubmitTitle?: string;
  isUpdateNameRequired?: boolean;
}

export enum UpdateVendorFormFieldsMap {
  BUSINESS_NAME = 'businessName',
  EMAIL = 'email',
  INTER_COUNTRY = 'interCountry',
  SWIFT = 'swift',
  IBAN = 'iban',
  INTER_PHONE = 'interPhone',
  INTER_PHONE_STATUS = 'interPhoneStatus',
  INTER_PHONE_REGION_CODE = 'interPhoneRegionCode',
  INTER_LINE_1 = 'interLine1',
  INTER_LINE_2 = 'interLine2',
  INTER_CITY = 'interCity',
  INTER_STATE = 'interState',
  INTER_POSTAL_CODE = 'interPostalCode',
  LINE_1 = 'line1',
  LINE_2 = 'line2',
  CITY = 'city',
  STATE = 'state',
  POSTAL_CODE = 'postalCode',
  ACH_ACCOUNT_NUMBER = 'achAccountNumber',
  ACH_ROUTING_NUMBER = 'achRoutingNumber',
  WIRE_ACCOUNT_NUMBER = 'wireAccountNumber',
  WIRE_ROUTING_NUMBER = 'wireRoutingNumber',
  NOTIFY_VENDOR = 'notifyVendor',
}

interface SubDivision {
  name: string;
  code: string;
}

export interface ISupportedCountry {
  name: string;
  code: string;
  nationalIdDescription: string;
  ibanSpecificValidationRequired?: string;
  postalCodeValidationExample?: string;
  idValidationRegex: string;
  idValidationDescription: string;
  postalCodeValidationRegex: string;
  postalCodeValidationDescription: string;
  phoneNumberValidationRegex: string;
  ibanValidationRegex: string | null;
  swiftCodeValidationRegex: string;
  subDivisions: SubDivision[];
}

export interface IUseFormLogic {
  moveType: MoveMoneyTypes;
  formType: FormTypes;
  vendorData?: IVendorModel;
  initialBusinessName?: string;
  isUpdateNameRequired?: boolean;
  isVendorUpdateMandatory?: boolean;
}

export interface IGetDefaultValues {
  moveType: MoveMoneyTypes;
  vendorData?: IVendorModel;
  preFilledBusinessName?: string;
}

export interface UpdateVendorFormProps {
  [UpdateVendorFormFieldsMap.BUSINESS_NAME]: string;
  [UpdateVendorFormFieldsMap.EMAIL]: string;
  [UpdateVendorFormFieldsMap.INTER_COUNTRY]: string;
  [UpdateVendorFormFieldsMap.SWIFT]: string;
  [UpdateVendorFormFieldsMap.IBAN]: string;
  [UpdateVendorFormFieldsMap.INTER_PHONE]: string;
  [UpdateVendorFormFieldsMap.INTER_PHONE_STATUS]: boolean;
  [UpdateVendorFormFieldsMap.INTER_PHONE_REGION_CODE]: RegionCode | null;
  [UpdateVendorFormFieldsMap.INTER_LINE_1]: string;
  [UpdateVendorFormFieldsMap.INTER_LINE_2]: string;
  [UpdateVendorFormFieldsMap.INTER_CITY]: string;
  [UpdateVendorFormFieldsMap.INTER_STATE]: string;
  [UpdateVendorFormFieldsMap.INTER_POSTAL_CODE]: string;
  [UpdateVendorFormFieldsMap.LINE_1]: string;
  [UpdateVendorFormFieldsMap.LINE_2]: string;
  [UpdateVendorFormFieldsMap.CITY]: string;
  [UpdateVendorFormFieldsMap.STATE]: string;
  [UpdateVendorFormFieldsMap.POSTAL_CODE]: string;
  [UpdateVendorFormFieldsMap.ACH_ACCOUNT_NUMBER]: string;
  [UpdateVendorFormFieldsMap.ACH_ROUTING_NUMBER]: string;
  [UpdateVendorFormFieldsMap.WIRE_ACCOUNT_NUMBER]: string;
  [UpdateVendorFormFieldsMap.WIRE_ROUTING_NUMBER]: string;
  [UpdateVendorFormFieldsMap.NOTIFY_VENDOR]: boolean;
}

export interface IFormSwitch {
  formMethods: ExtendedUseFormReturn<UpdateVendorFormProps>;
  moveType: MoveMoneyTypes;
  formType: FormTypes;
  billId: string;
  isVendorUpdateMandatory?: boolean;
  vendorData?: IVendorModel;
  isUpdateNameRequired?: boolean;
}

export interface CountryData {
  supportedCountries: {
    countries: ISupportedCountry[];
  };
}

export interface IInternationalData {
  swiftCode: string;
  iban: string;
  internationalPhoneNumber: string;
  internationalSupplierAddressData: {
    city: string;
    state: string | null;
    line1: string;
    line2?: string | null;
    postalCode: string;
    country: string;
  };
}

export interface IFormWrapperProps {
  formMethods: ExtendedUseFormReturn<UpdateVendorFormProps>;
  billId: string;
  formType: FormTypes;
  isVendorUpdateMandatory?: boolean;
  moveType: MoveMoneyTypes;
  vendorData?: IVendorModel;
  isUpdateNameRequired?: boolean;
}
